import React, { useState, useMemo } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    IconButton,
    Collapse,
    Chip,
    Tooltip, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button
} from "@mui/material";
import {
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    FitnessCenter as TemplateIcon,
    Assessment as AnalyticsIcon,
    Save as SaveIcon,
    Groups as MuscleIcon,
    Person as PersonIcon,
    Star as ExperienceIcon,
    Accessibility as BodyPartIcon,
    Build as EquipmentIcon,
    AccessTime as TimeIcon,
    ContentCopy as DuplicateIcon,
    Delete as DeleteIcon,
    AdminPanelSettings as PermissionsIcon,
    Lock as LockIcon,
    PersonAdd as AssignIcon
} from "@mui/icons-material";

import TemplateMetadataSection from "./TemplateMetadataSection";
import SecondaryMusclesBreakdown from "./SecondaryMusclesBreakdown";
import {deleteTemplate, duplicateTemplate} from "../../services/TemplatesService";
import {useNavigate} from "react-router-dom";
import {experienceLevels} from "../../enums/ExperienceLevels";
import {RTL} from "../../cache/CacheRtl";
import {bodyParts} from "../../enums/BodyParts";
import {EquipmentTypes} from "../../enums/EquipmentType";
import {SecondaryMuscles} from "../../enums/SecondaryMuscles";
import EditPermissionsDialog from "./EditPermissionsDialog";
import UserMultiSelectDialog from "../shared/UserMultiSelectDialog";
import GroupAdminCompaniesField from "../groupadmin/GroupAdminCompaniesField";
import {assignTemplateToTrainees, assignTemplateToAllFiltered} from "../../services/PlanSerice";
import {useAuth} from "../../contexts/AuthContext";
import {getErrorMessage} from "../../enums/ErrorMessages";

const CollapsibleTemplateOverview = ({
                                         template,
                                         hasUnsavedChanges,
                                         saving,
                                         handleSaveTemplate,
                                         onTemplateUpdate,
                                         onAssignCompanies,
                                         currentUserRole = 'SYSTEM_ADMIN'
                                     }) => {
    const [expanded, setExpanded] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    // COMPANY_GROUP_ADMIN only: assign the program to authorized companies (their equivalent of "assign to
    // trainees"). Kept separate from the trainee dialog so the company/super-admin flows are untouched.
    const [companyAssignOpen, setCompanyAssignOpen] = useState(false);
    const [assignCompanies, setAssignCompanies] = useState([]);
    const [duplicating, setDuplicating] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [actionSuccess, setActionSuccess] = useState(null);
    const {isSuperAdmin, isCompanyGroupAdmin} = useAuth();

    const navigate = useNavigate();

    // A program authored by a COMPANY_GROUP_ADMIN carries ownerUserId/ownerName. It is read-only to everyone
    // except its owner (a group admin only ever loads their OWN owned programs, so being a group admin here
    // implies ownership) — mirroring how a system template is read-only to non-super-admins.
    const isGroupAdminOwned = !!template?.ownerUserId;

    const getDurationDisplay = (duration) => {
        switch (duration) {
            case 'SMALL': return '30-45 דקות';
            case 'MEDIUM': return '45-60 דקות';
            case 'LARGE': return '60+ דקות';
            default: return null;
        }
    };

    // Calculate muscle breakdown
    const calculateMuscleBreakdown = () => {
        if (!template?.workouts) return { totalSets: 0, muscleCount: 0 };

        const muscleBreakdown = {};

        template.workouts.forEach(workout => {
            workout.exercises?.forEach(exercise => {
                if (exercise.secondaryMuscle && exercise.numberOfSets) {
                    const muscle = exercise.secondaryMuscle;
                    if (!muscleBreakdown[muscle]) {
                        muscleBreakdown[muscle] = 0;
                    }
                    muscleBreakdown[muscle] += exercise.numberOfSets;
                }
            });
        });

        const totalSets = Object.values(muscleBreakdown).reduce((sum, sets) => sum + sets, 0);
        const muscleCount = Object.keys(muscleBreakdown).length;

        return { totalSets, muscleCount, muscleBreakdown };
    };

    // Calculate permissions summary.
    // NOTE ordering: server semantics are `null companyPermissions` === "all companies"
    // (GenericPermissionService.getResourceCompanies: null → ALL; empty array → system-admin
    // only). A previous `if (!template?.companyPermissions) return {totalCompanies: 0}` guard
    // ran FIRST and shadowed the null→'ALL' branch, so an all-companies template wrongly showed
    // no chip. The `=== null` (ALL) check must precede the falsy catch-all — matching the
    // correct sibling ExercisePage.getPermissionsDisplayText. Strict `=== null` (not falsy) so a
    // freshly-created, not-yet-populated template (`undefined`) falls through to the 0 fallback.
    const calculatePermissionsSummary = () => {
        // All companies (server sends null for "shared with everyone").
        if (template?.companyPermissions === null) {
            return { totalCompanies: 'ALL' };
        }

        if (Array.isArray(template?.companyPermissions)) {
            if (template.companyPermissions.length === 0) {
                return { totalCompanies: 'ADMIN_ONLY' }; // System admin only
            }
            return { totalCompanies: template.companyPermissions.length }; // Specific companies
        }

        // undefined / unexpected non-array (e.g. an unsaved template) → treat as none.
        return { totalCompanies: 0 };
    };

    const { totalSets, muscleCount } = useMemo(() => calculateMuscleBreakdown(), [template?.workouts]);
    const { totalCompanies } = useMemo(() => calculatePermissionsSummary(), [template?.companyPermissions]);
    const durationText = getDurationDisplay(template?.trainDuration);

    // Check if template can be modified
    const canModifyTemplate = () => {
        if (template?.systemResource) return isSuperAdmin(); // System templates: super admins only
        if (isGroupAdminOwned) return isCompanyGroupAdmin(); // Group-admin programs: only the owning group admin
        return true; // Ordinary company templates can be modified by anyone with access
    };

    // Check if template can be saved
    const canSaveTemplate = () => {
        if (!hasUnsavedChanges || saving) return false;
        return canModifyTemplate();
    };

    // Get save button tooltip
    const getSaveTooltip = () => {
        if (!hasUnsavedChanges) return "אין שינויים לשמירה";
        if (!canModifyTemplate()) {
            return isGroupAdminOwned
                ? `תוכנית זו נוצרה על ידי ${template?.ownerName || "מנהל קבוצת חברות"} וזמינה לקריאה בלבד`
                : "רק מנהל מערכת יכול לערוך תבניות מערכת";
        }
        return "שמור שינויים";
    };

    const handleDuplicate = async () => {
        if (!template.id) {
            setActionError("לא ניתן לשכפל תוכנית שלא נשמרה");
            return;
        }

        setDuplicating(true);
        setActionError(null);

        try {
            const response = await duplicateTemplate(template.id);

            // Navigate to the new template page. A success response with no id is not a silent no-op —
            // without this the spinner just stops and the user sees nothing happen.
            if (response.data) {
                navigate(`/app/templates/${response.data}`);
            } else {
                setActionError("שגיאה בשכפול התוכנית. אנא נסה שוב.");
            }
        } catch (error) {
            console.error("Error duplicating template:", error);
            // Distinguish "not allowed" from a transient failure — a 403 here means the template is
            // outside your library (e.g. another company's), and retrying will never help.
            const status = error?.response?.status;
            const errorCode = error?.response?.data?.errorCode;
            if (status === 403) {
                setActionError("אין לך הרשאה לשכפל תוכנית זו.");
            } else if (errorCode) {
                setActionError(getErrorMessage(errorCode));
            } else {
                setActionError("שגיאה בשכפול התוכנית. אנא נסה שוב.");
            }
        } finally {
            setDuplicating(false);
        }
    };

    const handleDelete = async () => {
        if (!template.id) {
            setActionError("לא ניתן למחוק תוכנית שלא נשמרה");
            return;
        }

        // Check if user can delete system resource templates
        if (template.systemResource && !isSuperAdmin()) {
            setActionError("רק מנהל מערכת יכול למחוק תבניות מערכת");
            setDeleteDialogOpen(false);
            return;
        }

        setDeleting(true);
        setActionError(null);

        try {
            await deleteTemplate(template.id);

            navigate("/app/templates");

            setDeleteDialogOpen(false);
        } catch (error) {
            console.error("Error deleting template:", error);
            setActionError("שגיאה במחיקת התוכנית. אנא נסה שוב.");
            setDeleteDialogOpen(false);
        } finally {
            setDeleting(false);
        }
    };

    // Handle assign template to trainees
    const handleAssignToTrainees = async (selectedUsers, options = {}) => {
        if (!template.id || selectedUsers.length === 0) {
            return;
        }

        setAssigning(true);
        setActionError(null);
        setActionSuccess(null);

        try {
            const traineeIds = selectedUsers.map(user => user.id);
            const includeRmWeek = options.includeRmWeek || false;
            const advancedSettings = options.advancedSettings || null;
            const result = await assignTemplateToTrainees(template.id, traineeIds, includeRmWeek, advancedSettings);

            // Handle response - server should return { successCount, failureCount, failures? }
            const successCount = result?.successCount ?? traineeIds.length;
            const failureCount = result?.failureCount ?? 0;

            if (failureCount === 0) {
                setActionSuccess(`התוכנית הוקצתה בהצלחה ל-${successCount} מתאמנים`);
            } else if (successCount > 0) {
                setActionSuccess(`התוכנית הוקצתה ל-${successCount} מתאמנים. ${failureCount} נכשלו.`);
            } else {
                setActionError(`הקצאת התוכנית נכשלה לכל ${failureCount} המתאמנים`);
            }

            setAssignDialogOpen(false);
        } catch (error) {
            console.error("Error assigning template:", error);
            setActionError("שגיאה בהקצאת התוכנית. אנא נסה שוב.");
        } finally {
            setAssigning(false);
        }
    };

    // Open the company-assignment dialog seeded with the program's current sharing set (group admin only).
    const openCompanyAssign = () => {
        setAssignCompanies(Array.isArray(template?.companyPermissions) ? template.companyPermissions : []);
        setCompanyAssignOpen(true);
    };

    // Persist the group admin's company selection. Delegates to the parent (NewTemplatePage) which cleans
    // and PUTs the program with the new companyPermissions — an empty selection unassigns it (kept private
    // to the group admin's library).
    const handleAssignToCompanies = async () => {
        if (!template?.id) return;
        setAssigning(true);
        setActionError(null);
        setActionSuccess(null);
        try {
            await onAssignCompanies(assignCompanies);
            setActionSuccess(
                assignCompanies.length === 0
                    ? "השיוך עודכן: התוכנית אינה משויכת כעת לאף חברה"
                    : `התוכנית שויכה ל-${assignCompanies.length} חברות`
            );
            setCompanyAssignOpen(false);
        } catch (error) {
            console.error("Error assigning template to companies:", error);
            setActionError("שגיאה בשיוך התוכנית לחברות. אנא נסה שוב.");
        } finally {
            setAssigning(false);
        }
    };

    // Handle "Select All" server-side assignment
    const handleSelectAllAssignment = async (selectionData, options = {}) => {
        if (!template.id) return;

        setAssigning(true);
        setActionError(null);
        setActionSuccess(null);

        try {
            const includeRmWeek = options.includeRmWeek || false;
            const advancedSettings = options.advancedSettings || null;

            const result = await assignTemplateToAllFiltered(
                template.id,
                selectionData.filters,
                includeRmWeek,
                advancedSettings
            );

            const successCount = result?.successCount ?? 0;
            const failureCount = result?.failureCount ?? 0;

            if (failureCount === 0) {
                setActionSuccess(`התוכנית הוקצתה בהצלחה ל-${successCount} מתאמנים`);
            } else if (successCount > 0) {
                setActionSuccess(`התוכנית הוקצתה ל-${successCount} מתאמנים. ${failureCount} נכשלו.`);
            } else {
                setActionError(`הקצאת התוכנית נכשלה לכל ${failureCount} המתאמנים`);
            }

            setAssignDialogOpen(false);
        } catch (error) {
            console.error("Error in bulk assignment:", error);
            setActionError("שגיאה בהקצאת התוכנית. אנא נסה שוב.");
        } finally {
            setAssigning(false);
        }
    };

    // Handle permissions save - updates template state and will be saved with main template
    const handlePermissionsSave = async (updatedTemplate) => {
        try {
            // Check if user can modify system resource templates
            if (template.systemResource && !isSuperAdmin()) {
                throw new Error("רק מנהל מערכת יכול לערוך הרשאות של תבניות מערכת");
            }

            // Update the template in the parent component - this will trigger unsaved changes
            // and the permissions will be saved when the user saves the template
            onTemplateUpdate(updatedTemplate);

            console.log('Permissions updated:', {
                systemResource: updatedTemplate.systemResource,
                companyPermissions: updatedTemplate.companyPermissions
            });
        } catch (error) {
            console.error('Error updating permissions:', error);
            throw error; // Re-throw to let the dialog handle the error
        }
    };

    // Enhanced save handler that checks system resource permissions
    const handleSaveTemplateWithValidation = () => {
        if (!canSaveTemplate()) {
            if (template?.systemResource && !isSuperAdmin()) {
                setActionError("רק מנהל מערכת יכול לערוך תבניות מערכת");
                return;
            }
        }
        handleSaveTemplate();
    };

    const isTemplateComplete = () => {
        return template?.name &&
            template?.nameHeb &&
            template?.experienceLevel &&
            template?.bodyPart;
    };

    // Check if user can manage permissions
    const canManagePermissions = () => {
        if (!template?.id) return false;
        if (!canModifyTemplate()) return false; // read-only (system or group-admin-authored) → no permission edits
        return isSuperAdmin();
    };

    // Check if user can delete template
    const canDeleteTemplate = () => {
        if (!template?.id) return false;
        return canModifyTemplate(); // a read-only program (system or group-admin-authored) cannot be deleted here
    };

    // Get permissions display text
    const getPermissionsDisplayText = () => {
        if (totalCompanies === 'ALL') {
            return 'כל החברות';
        } else if (totalCompanies === 'ADMIN_ONLY') {
            return 'מנהל בלבד';
        } else if (typeof totalCompanies === 'number' && totalCompanies > 0) {
            return `${totalCompanies} חברות`;
        }
        return null;
    };

    return (
        <Container maxWidth={false} sx={{ px: 3, pt: 2 }}>
            <Paper
                sx={{
                    backgroundColor: "#FCFCFD",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    direction: "rtl",
                    mb: 2,
                    // Add visual indication for read-only templates (system OR group-admin-authored)
                    ...(!canModifyTemplate() && {
                        backgroundColor: "#F8F9FA",
                        border: "1px solid #FFC107"
                    })
                }}
                elevation={0}
            >
                {/* Compact Header - Always Visible */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 20px",
                        backgroundColor: !canModifyTemplate()
                            ? "#FFF8E1"
                            : "#F8FAFC",
                        borderBottom: expanded ? "1px solid #E2E8F0" : "none",
                        cursor: "pointer"
                    }}
                    onClick={() => setExpanded(!expanded)}
                >

                    {/* Error Alert */}
                    {actionError && (
                        <Alert
                            severity="error"
                            onClose={() => setActionError(null)}
                            sx={{ m: 2, mb: 0 }}
                        >
                            {actionError}
                        </Alert>
                    )}

                    {/* Left side - Template info */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <TemplateIcon sx={{ color: "#64748B", fontSize: "18px" }} />
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    color: "#1E293B",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }}
                            >
                                {template?.nameHeb || "תוכנית חדשה"}
                            </Typography>
                            {!isTemplateComplete() && (
                                <Chip
                                    label="חסרים פרטים"
                                    size="small"
                                    sx={{
                                        backgroundColor: "#FEF3C7",
                                        color: "#D97706",
                                        fontWeight: 600,
                                        fontSize: "10px",
                                        height: "18px"
                                    }}
                                />
                            )}
                            {/* System Resource indicator */}
                            {template?.systemResource && (
                                <Chip
                                    icon={!canModifyTemplate() ? <LockIcon sx={{ fontSize: "10px !important" }} /> : undefined}
                                    label={!canModifyTemplate() ? "תבנית מערכת (קריאה בלבד)" : "תבנית מערכת"}
                                    size="small"
                                    sx={{
                                        backgroundColor: !canModifyTemplate() ? "#FEF3C7" : "#DBEAFE",
                                        color: !canModifyTemplate() ? "#D97706" : "#1E40AF",
                                        fontWeight: 600,
                                        fontSize: "10px",
                                        height: "18px"
                                    }}
                                />
                            )}
                            {/* Group-admin author indicator: read-only "program by <owner>" for the assigned
                                company / system admin (mirrors the system-template read-only label). */}
                            {!template?.systemResource && isGroupAdminOwned && !canModifyTemplate() && (
                                <Chip
                                    icon={<LockIcon sx={{ fontSize: "10px !important" }} />}
                                    label={`תוכנית מאת ${template.ownerName} (קריאה בלבד)`}
                                    size="small"
                                    sx={{
                                        backgroundColor: "#FEF3C7",
                                        color: "#D97706",
                                        fontWeight: 600,
                                        fontSize: "10px",
                                        height: "18px"
                                    }}
                                />
                            )}
                        </Box>

                        {/* Quick stats */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: 2 }}>
                            <RTL>
                                {template?.gender && (
                                    <Chip
                                        icon={<PersonIcon sx={{ fontSize: "12px !important" }} />}
                                        label={template.gender === 'MALE' ? 'זכר' : template.gender === 'FEMALE' ? 'נקבה' : template.gender}
                                        size="small"
                                        sx={{
                                            backgroundColor: template.gender === 'MALE' ? "#E0F7FA" : "#FCE4EC",
                                            color: template.gender === 'MALE' ? "#00695C" : "#C2185B",
                                            fontSize: "11px",
                                            height: "20px"
                                        }}
                                    />
                                )}
                            </RTL>
                            <RTL>
                                {template?.experienceLevel && (
                                    <Chip
                                        icon={<ExperienceIcon sx={{ fontSize: "12px !important" }} />}
                                        label={experienceLevels.find(level => level.value === template.experienceLevel)?.name || template.experienceLevel}
                                        size="small"
                                        sx={{
                                            backgroundColor: "#E0F2FE",
                                            color: "#0369A1",
                                            fontSize: "11px",
                                            height: "20px",
                                            "& .MuiChip-icon": { fontSize: "12px" }
                                        }}
                                    />
                                )}
                            </RTL>
                            <RTL>
                                {template?.equipmentType && (
                                    <Chip
                                        icon={<EquipmentIcon sx={{ fontSize: "12px !important" }} />}
                                        label={EquipmentTypes.find(level => level.value === template.equipmentType)?.name || template.equipmentType}
                                        size="small"
                                        sx={{
                                            backgroundColor: "#F1F5F9",
                                            color: "#475569",
                                            fontSize: "11px",
                                            height: "20px"
                                        }}
                                    />
                                )}
                            </RTL>
                            <RTL>
                                {template?.bodyPart && (
                                    <Chip
                                        icon={<BodyPartIcon sx={{ fontSize: "12px !important" }} />}
                                        label={bodyParts.find(level => level.value === template.bodyPart)?.name || template.bodyPart}
                                        size="small"
                                        sx={{
                                            backgroundColor: "#F0F9FF",
                                            color: "#0284C7",
                                            fontSize: "11px",
                                            height: "20px"
                                        }}
                                    />
                                )}
                            </RTL>

                            <RTL>
                                {template?.secondaryMuscle && (
                                    <Chip
                                        icon={<MuscleIcon sx={{ fontSize: "12px !important" }} />}
                                        label={
                                            (SecondaryMuscles.find(sm => sm.value === template.secondaryMuscle)?.name || template.secondaryMuscle) +
                                            (template?.secondaryMuscle2 ? " + " + (SecondaryMuscles.find(sm => sm.value === template.secondaryMuscle2)?.name || template.secondaryMuscle2) : "")
                                        }
                                        size="small"
                                        sx={{
                                            backgroundColor: "#ECFDF5",
                                            color: "#047857",
                                            fontSize: "11px",
                                            height: "20px"
                                        }}
                                    />
                                )}
                            </RTL>

                            <RTL>
                                {muscleCount > 0 && (
                                    <Chip
                                        icon={<AnalyticsIcon sx={{ fontSize: "12px !important" }} />}
                                        label={`${muscleCount} שרירים • ${totalSets} סטים`}
                                        size="small"
                                        sx={{
                                            backgroundColor: "#DBEAFE",
                                            color: "#1E40AF",
                                            fontSize: "11px",
                                            height: "20px"
                                        }}
                                    />
                                )}
                            </RTL>

                            <RTL>
                                {durationText && (
                                    <Chip
                                        icon={<TimeIcon sx={{ fontSize: "12px !important" }} />}
                                        label={durationText}
                                        size="small"
                                        sx={{
                                            backgroundColor: "#F3F4F6",
                                            color: "#374151",
                                            fontSize: "11px",
                                            height: "20px"
                                        }}
                                    />
                                )}
                            </RTL>

                            {/* Permissions summary */}
                            {isSuperAdmin() && <RTL>
                                {getPermissionsDisplayText() && (
                                    <Chip
                                        icon={<PermissionsIcon sx={{ fontSize: "12px !important" }} />}
                                        label={getPermissionsDisplayText()}
                                        size="small"
                                        sx={{
                                            backgroundColor: "#FEF3C7",
                                            color: "#D97706",
                                            fontSize: "11px",
                                            height: "20px"
                                        }}
                                    />
                                )}
                            </RTL>}
                        </Box>
                    </Box>

                    {/* Right side - Actions */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {/* Permissions Button */}
                        {canManagePermissions() && (
                            <Tooltip title="ניהול הרשאות">
                                <span>
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setPermissionsDialogOpen(true);
                                        }}
                                        disabled={!template.id || !canManagePermissions()}
                                        sx={{
                                            color: "#64748B",
                                            "&:hover": {
                                                backgroundColor: "#FEF3C7",
                                                color: "#D97706"
                                            },
                                            "&:disabled": {
                                                color: "#CBD5E1"
                                            }
                                        }}
                                        size="small"
                                    >
                                        <PermissionsIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}

                        {/* Delete Button */}
                        <Tooltip title={canDeleteTemplate() ? "מחק תוכנית" : "רק מנהל מערכת יכול למחוק תבניות מערכת"}>
                            <span>
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteDialogOpen(true);
                                    }}
                                    disabled={!template?.id || deleting || !canDeleteTemplate()}
                                    sx={{
                                        color: "#64748B",
                                        "&:hover": {
                                            backgroundColor: "#FEE2E2",
                                            color: "#EF4444"
                                        },
                                        "&:disabled": {
                                            color: "#CBD5E1"
                                        }
                                    }}
                                    size="small"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        {/* Assign Button — a group admin assigns to their authorized COMPANIES (they have no
                            trainees); everyone else assigns to TRAINEES. */}
                        <Tooltip title={isCompanyGroupAdmin() ? "שיוך לחברות מורשות" : "הקצה למתאמנים"}>
                            <span>
                                <IconButton
                                    aria-label={isCompanyGroupAdmin() ? "שיוך לחברות מורשות" : "הקצה למתאמנים"}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (isCompanyGroupAdmin()) {
                                            openCompanyAssign();
                                        } else {
                                            setAssignDialogOpen(true);
                                        }
                                    }}
                                    disabled={!template?.id || assigning}
                                    sx={{
                                        color: "#64748B",
                                        "&:hover": {
                                            backgroundColor: "#DBEAFE",
                                            color: "#2563EB"
                                        },
                                        "&:disabled": {
                                            color: "#CBD5E1"
                                        }
                                    }}
                                    size="small"
                                >
                                    <AssignIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        {/* Duplicate — intentionally enabled even on read-only (system / group-admin-authored)
                            templates: forking one is allowed, and the server pins the copy to your own company. */}
                        <Tooltip title="שכפל תוכנית">
                            <span>
                                <IconButton
                                    aria-label="שכפל תוכנית"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDuplicate();
                                    }}
                                    disabled={!template?.id || duplicating}
                                    sx={{
                                        color: "#64748B",
                                        "&:hover": {
                                            backgroundColor: "#E2E8F0",
                                            color: "#475569"
                                        },
                                        "&:disabled": {
                                            color: "#CBD5E1"
                                        }
                                    }}
                                    size="small"
                                >
                                    <DuplicateIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title={getSaveTooltip()}>
                            <span>
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSaveTemplateWithValidation();
                                    }}
                                    disabled={!canSaveTemplate()}
                                    sx={{
                                        backgroundColor: canSaveTemplate() ? '#2563eb' : 'transparent',
                                        color: canSaveTemplate() ? 'white' : '#9CA3AF',
                                        '&:hover': {
                                            backgroundColor: canSaveTemplate() ? '#1d4ed8' : '#F3F4F6'
                                        },
                                        '&:disabled': {
                                            backgroundColor: 'transparent',
                                            color: '#D1D5DB'
                                        },
                                        width: 32,
                                        height: 32
                                    }}
                                    size="small"
                                >
                                    <SaveIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title={expanded ? "הסתר פרטים" : "הצג פרטים"}>
                            <IconButton
                                sx={{
                                    color: "#64748B",
                                    "&:hover": {
                                        backgroundColor: "#E2E8F0",
                                        color: "#475569"
                                    }
                                }}
                                size="small"
                            >
                                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Expanded Content */}
                <Collapse in={expanded}>
                    <Box sx={{ p: 3 }}>
                        <TemplateMetadataSection
                            handleSaveTemplate={handleSaveTemplateWithValidation}
                            hasUnsavedChanges={hasUnsavedChanges}
                            saving={saving}
                            template={template}
                            onTemplateUpdate={onTemplateUpdate}
                            readOnly={template?.systemResource && !canModifyTemplate()}
                        />
                    </Box>
                </Collapse>
            </Paper>
            <Box sx={{ mt: 3 }}>
                <SecondaryMusclesBreakdown template={template} />
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                sx={{ direction: 'rtl' }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ textAlign: 'right', fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                    מחיקת תוכנית
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'right', fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                    <Typography>
                        האם אתה בטוח שברצונך למחוק את התוכנית "{template?.nameHeb || template?.name}"?
                    </Typography>
                    {template?.systemResource && (
                        <Typography sx={{ mt: 1, color: '#F59E0B', fontWeight: 500 }}>
                            זוהי תבנית מערכת - המחיקה תשפיע על כל המשתמשים במערכת!
                        </Typography>
                    )}
                    <Typography sx={{ mt: 1, color: '#EF4444', fontWeight: 500 }}>
                        פעולה זו לא ניתנת לביטול!
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ direction: 'ltr', gap: 1, p: 2 }}>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={deleting}
                        variant="outlined"
                    >
                        ביטול
                    </Button>
                    <Button
                        onClick={handleDelete}
                        disabled={deleting}
                        variant="contained"
                        color="error"
                        sx={{ minWidth: 100 }}
                    >
                        {deleting ? 'מוחק...' : 'מחק'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Permissions Dialog - only mount when opened */}
            {permissionsDialogOpen && (
                <EditPermissionsDialog
                    open={permissionsDialogOpen}
                    onClose={() => setPermissionsDialogOpen(false)}
                    onSave={handlePermissionsSave}
                    template={template}
                />
            )}

            {/* Assign to Trainees Dialog - only mount when opened */}
            {assignDialogOpen && (
                <UserMultiSelectDialog
                    isOpen={assignDialogOpen}
                    onClose={() => setAssignDialogOpen(false)}
                    onConfirm={handleAssignToTrainees}
                    onSelectAllConfirm={handleSelectAllAssignment}
                    showSelectAll={true}
                    title={`הקצאת תוכנית: ${template?.nameHeb || template?.name || ''}`}
                    confirmButtonText="הקצה תוכנית"
                    isLoading={assigning}
                    showRmWeekOption={true}
                    showAdvancedSettings={true}
                    templateId={template?.id}
                />
            )}

            {/* Assign to Companies Dialog (group admin) - only mount when opened */}
            {companyAssignOpen && (
                <Dialog
                    open={companyAssignOpen}
                    onClose={() => setCompanyAssignOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    sx={{ '& .MuiDialog-paper': { direction: 'rtl' } }}
                >
                    <DialogTitle sx={{ textAlign: 'right', fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                        שיוך התוכנית לחברות מורשות
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 1 }}>
                            <GroupAdminCompaniesField
                                value={assignCompanies}
                                onChange={setAssignCompanies}
                                disabled={assigning}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ direction: 'ltr', gap: 1, p: 2 }}>
                        <Button
                            onClick={() => setCompanyAssignOpen(false)}
                            disabled={assigning}
                            variant="outlined"
                        >
                            ביטול
                        </Button>
                        <Button
                            onClick={handleAssignToCompanies}
                            disabled={assigning}
                            variant="contained"
                            sx={{ minWidth: 110 }}
                        >
                            {assigning ? 'שומר...' : 'שמור שיוך'}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Success Snackbar */}
            {actionSuccess && (
                <Alert
                    severity="success"
                    onClose={() => setActionSuccess(null)}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 9999,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                >
                    {actionSuccess}
                </Alert>
            )}
        </Container>
    );
};

export default CollapsibleTemplateOverview;